import { rem } from "@certn/cascadia/utils";
import {
  CascadiaSpacing,
  CascadiaStandardIcon,
  CascadiaTypographyStyle,
} from "@certn/cascadia-tokens";
import { slugify } from "@certn/utils";
import { css } from "@emotion/css";
import { useContext, useRef } from "react";
import { AriaButtonProps, useButton } from "react-aria";

import { NavigationContext } from "../../../context/NavigationProvider";
import { Space, useLayout } from "../../../foundations/grid-layout/Layout";
import { Icon } from "../../../foundations/icons/Icon/Icon";
import { Text } from "../../../foundations/typography/Text";
import { NewBadge } from "../../badge/NewBadge";
import { useGetAllFocusShadows } from "../../utils";

export interface NavigationItemProps extends AriaButtonProps {
  title: string;
  badgeCount?: number;
  icon?: CascadiaStandardIcon;
  isActive?: boolean;
  style?: CascadiaTypographyStyle;
}

const iconSize: CascadiaSpacing = "space-5";

export function NavigationItem(props: NavigationItemProps) {
  const { icon, title, badgeCount, isActive, onPress } = props;
  const { isCollapsed } = useContext(NavigationContext);
  const ref = useRef<HTMLButtonElement>(null);
  const getAllFocusShadows = useGetAllFocusShadows();
  const { buttonProps } = useButton({ ...props, onPress }, ref);
  const { toggleMenu } = useLayout();

  return (
    <button
      data-testid={`sidebar-${slugify(title)}-button`}
      className={css(
        styles.container,
        css({
          "&:hover": {
            backgroundColor: "var(--background-ghost-hover)",
          },
          "&:focus-within": getAllFocusShadows(),
        }),
        isActive && styles.active
      )}
      data-collapsed={isCollapsed}
      {...buttonProps}
      onClick={(e) => {
        toggleMenu(false);
        buttonProps.onClick && buttonProps.onClick(e);
      }}
      ref={ref}
    >
      <div data-collapsed={isCollapsed} className={styles.leftWrapper}>
        {icon && (
          <div>
            <Icon
              className={styles.iconSize}
              name={icon}
              color={isActive ? "icon-accent-tofino" : "icon-weak"}
              padding="space-1-5"
            />
          </div>
        )}
        <Text
          truncate
          style={props.style ?? "summary/nav-list"}
          color={isActive ? "text-accent-tofino" : "text-base"}
        >
          {title}
        </Text>
      </div>
      <Space />
      {badgeCount && (
        <div className={css(styles.trailingWrapper)}>
          <NewBadge size="large" type="subtle">
            {badgeCount}
          </NewBadge>
        </div>
      )}
    </button>
  );
}

const styles = {
  container: css({
    all: "unset",
    cursor: "pointer",
    display: "flex",
    boxSizing: "border-box",
    gap: "var(--space-2)",
    justifyItems: "start",
    width: "100%",
    borderRadius: "var(--border-radius-3)",
    height: "40px",
    paddingLeft: "var(--space-2)",
    paddingRight: "var(--space-2)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    maxWidth: "100%",
    transition: "all 200ms ease",

    span: {
      transition: "opacity 200ms ease",
    },

    "&[data-collapsed=true]": {
      maxWidth: "40px",
      paddingLeft: "var(--space-1)",
      span: {
        opacity: 0,
      },
    },
  }),
  leftWrapper: css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    "&[data-collapsed=false]": {
      overflow: "hidden",
    },
  }),
  trailingWrapper: css({
    display: "flex",
    alignItems: "center",
  }),
  iconSize: css({
    width: rem(iconSize),
    height: rem(iconSize),
  }),
  active: css({ backgroundColor: "var(--background-accent-tofino-weak)" }),
};
